import { useEffect, useState } from 'react';
import Form, { Item, GroupItem, ColCountByScreen, RequiredRule, Label } from 'devextreme-react/form';
import { getSizeQualifier } from '../../../utils/media-query';
import { useAppSelector, useAppDispatch } from '../../../contexts/hooks';
import { CustomFormControl } from '../../utils/form-render/FormRender';
import { TextBox, Validator, Button } from 'devextreme-react';
import { formatMessage } from 'devextreme/localization';
import { BirthDateField } from './components/BirthDateField';
import { useApiPatient } from '../../../api/api.patient';
import { setPatient as setOrdersPatient } from '../../../contexts/slices/orders';
import { setPatient as setQuotationsPatient } from '../../../contexts/slices/quotations';
import { setPatient as setPreOrdersPatient } from '../../../contexts/slices/preorders';
import { setPatient as setAppointmentsPatient } from '../../../contexts/slices/appointments';
import { useConfigReady } from '../../../hooks/useConfigReady';
import ConfigLoading from '../../utils/config-loading/ConfigLoading';

const Index = ({ data, setData }) => {
  const { pending: configPending } = useConfigReady();
  const info = useAppSelector((x) => x.configuration.Data?.SystemFields)
    ?.filter((x) => x.Dfc_Tabla === 'Pacientes' && x.Dfc_Estado === true)
    .sort((a, b) => a.Dfc_Orden - b.Dfc_Orden);

  const ModePatient = useAppSelector((x) => x.globalOrders.CurrentOrder.ModePatient);
  const CurrentContext = useAppSelector((x) => x.globalOrders.CurrentOrder.ContextPatient);
  const DefDocument = useAppSelector((x) => x.globalOrders.CurrentOrder.NoFindedDocument);
  const {editDocument} = useApiPatient();
  const dispatch = useAppDispatch();

  // Estados para la edición de documento
  const [isEditingDocument, setIsEditingDocument] = useState(false);
  const [tempDocument, setTempDocument] = useState('');
  const [loadingDocumentEdit, setLoadingDocumentEdit] = useState(false);

  const [currentPatient, setCurrentPatient] = useState<any>({ AditionalFields: {}, Pc_Identificacion: DefDocument });
  const patientDataFromOrders = useAppSelector((x) => x.globalOrders.CurrentOrder.PatientData);
  const patientDataFromQuotations = useAppSelector((x) => x.globalQuotation.PatientData);
  const patientDataFromPreOrders = useAppSelector((x) => x.globalPreOrder.PatientData);
  const patientDataFromAppointments = useAppSelector((x) => x.globalAppointments.PatientData);

  // Lógica para establecer el estado de currentPatient sin usar useAppSelector dentro del useEffect
  useEffect(() => {
    switch (CurrentContext) {
      case 'orders':
        setCurrentPatient(patientDataFromOrders);
        break;
      case 'quotations':
        setCurrentPatient(patientDataFromQuotations);
        break;
      case 'preorders':
        setCurrentPatient(patientDataFromPreOrders);
        break;
      case 'appointment':
        setCurrentPatient(patientDataFromAppointments);
        break;
      default:
        break;
    }
  }, [CurrentContext, patientDataFromOrders, patientDataFromQuotations, patientDataFromPreOrders, patientDataFromAppointments, ModePatient]);

  // Actualización al guardar un nuevo paciente
  useEffect(() => {
    if (ModePatient === 'new') {
      // Establecer Pc_Identificacion como DefDocument en mayúsculas
      setCurrentPatient((prev) => ({ ...prev, Pc_Identificacion: DefDocument }));
    }
  }, [ModePatient, DefDocument]);

  const loading = useAppSelector((x) => x.globalOrders.CurrentOrder.LoadingPatient);

  // Funciones para edición de documento
  const handleStartEditDocument = () => {
    setTempDocument(currentPatient['Pc_Identificacion'] || '');
    setIsEditingDocument(true);
  };

  const handleDocumentChange = (e) => {
    setTempDocument(e.value.toUpperCase());
  };

  const handleCancelEditDocument = () => {
    setIsEditingDocument(false);
    setTempDocument('');
  };

  const handleSaveDocument = async () => {
    if (!tempDocument.trim()) return;
    
    setLoadingDocumentEdit(true);
    try {
      const response = await editDocument(tempDocument.trim(), Number(currentPatient.Pc_Id));
      
      if (response) {
        const updatedPatient = { ...currentPatient, Pc_Identificacion: tempDocument.trim() };
        
        // Actualizar el estado local
        setCurrentPatient(updatedPatient);
        
        // Actualizar el store según el contexto
        switch (CurrentContext) {
          case 'orders':
            dispatch(setOrdersPatient(updatedPatient));
            break;
          case 'quotations':
            dispatch(setQuotationsPatient(updatedPatient));
            break;
          case 'preorders':
            dispatch(setPreOrdersPatient(updatedPatient));
            break;
          case 'appointment':
            dispatch(setAppointmentsPatient(updatedPatient));
            break;
        }
        
        setIsEditingDocument(false);
        setTempDocument('');
      }
    } finally {
      setLoadingDocumentEdit(false);
    }
  };

  // Sin SystemFields el formulario se renderiza sin campos y permitiría guardar
  // un paciente incompleto. Mientras GetAllConfig no responda, mostrar la carga.
  if (configPending && !info?.length) {
    return <ConfigLoading message='Cargando campos del paciente...' />;
  }

  if (ModePatient === 'view' && currentPatient.Pc_Id) {
    if (Object.keys(currentPatient).length === 1) {
      return <div>Cargando ...</div>;
    }
    return (
      <div className='form-container'>
        <Form formData={currentPatient} screenByWidth={getSizeQualifier} labelLocation='top' labelMode='static' className='plain-styled-form' readOnly={true}>
          <GroupItem colCount={2}>
            <ColCountByScreen xs={2} sm={2} md={2} lg={2} />
            <Item>
              <TextBox stylingMode='outlined' maxLength={30} labelMode='static' value={currentPatient['Pc_Identificacion']} validationError={null} label={formatMessage('PatientField')} onValueChange={(e) => setData('Pc_Identificacion', 'UPPER')(e)} readOnly={true}>
                <Validator>
                  <RequiredRule message={`${formatMessage('PatientField')} ${formatMessage('requiredField')}`} />
                </Validator>
              </TextBox>
            </Item>
            {info
              ?.filter((x) => x.Dfc_Equivalencia !== 'Pc_Foto' && x.TipoCampo.Tcm_Equivalencia !== 'dxTextArea')
              .sort((x) => x.Dfc_Orden)
              .map((x) => {
                const currentDep = info.find((y) => x.Dfc_DependencyDefinition === y.Dfc_Id)?.Dfc_Equivalencia;
                const value = x.Dfc_Dinamico && currentPatient.AditionalFields ? currentPatient.AditionalFields[x.Dfc_Equivalencia] || null : currentPatient[x.Dfc_Equivalencia];

                // Campo especial para fecha de nacimiento
                if (x.Dfc_Equivalencia === 'Pc_FechaNacimiento') {
                  return (
                    <Item key={x.Dfc_Id} colSpan={2}>
                      <BirthDateField
                        value={value}
                        onValueChange={(newValue) => {
                          const setValue = setData(x.Dfc_Dinamico ? `AditionalFields.${x.Dfc_Equivalencia}` : x.Dfc_Equivalencia, x.Dfc_Cast);
                          setValue(newValue);
                        }}
                        isEditing={false}
                        label={x.Dfc_Label || 'Fecha de Nacimiento'}
                      />
                    </Item>
                  );
                }
                return (
                  <Item key={x.Dfc_Id} colSpan={1}>
                    <CustomFormControl valueDepency={x.Dfc_Dependency && currentDep && currentPatient.AditionalFields ? currentPatient.AditionalFields[currentDep] : undefined} value={value} data={x} onValueChange={setData(x.Dfc_Dinamico ? `AditionalFields.${x.Dfc_Equivalencia}` : x.Dfc_Equivalencia, x.Dfc_Cast)} isEditing={false} />
                  </Item>
                );
              })}
          </GroupItem>
          <GroupItem>
            {info
              ?.filter((x) => x.TipoCampo.Tcm_Equivalencia === 'dxTextArea')
              .sort((x) => x.Dfc_Orden)
              .map((x) => {
                return (
                  <Item key={x.Dfc_Id}>
                    <CustomFormControl value={x.Dfc_Dinamico && currentPatient.AditionalFields ? currentPatient.AditionalFields[x.Dfc_Equivalencia] : currentPatient[x.Dfc_Equivalencia]} data={x} onValueChange={setData(x.Dfc_Dinamico ? `AditionalFields.${x.Dfc_Equivalencia}` : x.Dfc_Equivalencia, x.Dfc_Cast)} isEditing={false} />
                  </Item>
                );
              })}
          </GroupItem>
        </Form>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          .square-button .dx-button {
            min-width: 32px !important;
            width: 32px !important;
            height: 32px !important;
            border-radius: 4px !important;
          }
          
          .square-button .dx-button-content {
            border-radius: 4px !important;
          }
        `}
      </style>
      <Form screenByWidth={getSizeQualifier} labelLocation='left' className='plain-styled-form' disabled={loading}>
      <GroupItem colCount={2}>
        <ColCountByScreen xs={2} sm={2} md={2} lg={2} />
        <Item>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
            <div style={{ flex: 1 }}>
              <TextBox 
                stylingMode='outlined' 
                maxLength={30} 
                labelMode='static' 
                value={isEditingDocument ? tempDocument : currentPatient['Pc_Identificacion']} 
                validationError={null} 
                label={formatMessage('PatientField')} 
                onValueChanged={isEditingDocument ? handleDocumentChange : undefined}
                readOnly={!isEditingDocument}
                disabled={loadingDocumentEdit}
              >
                <Validator>
                  <RequiredRule message={`${formatMessage('PatientField')} ${formatMessage('requiredField')}`} />
                </Validator>
              </TextBox>
            </div>
            <div style={{ display: 'flex', gap: '2px', paddingBottom: '2px' }}>
              {loadingDocumentEdit ? (
                <button 
                  type="button"
                  className="dx-button dx-button-mode-outlined dx-button-default"
                  disabled={true}
                  title="Guardando..."
                  style={{
                    minWidth: '30px',
                    width: '30px',
                    height: '30px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    background: '#fff',
                    cursor: 'default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <i className="dx-icon-refresh" style={{ fontSize: '14px', color: '#ccc' }} />
                </button>
              ) : isEditingDocument ? (
                <>
                  <button 
                    type="button"
                    onClick={handleSaveDocument}
                    disabled={!tempDocument.trim()}
                    title="Guardar documento"
                    className="dx-button dx-button-mode-outlined dx-button-default"
                    style={{
                      minWidth: '30px',
                      width: '30px',
                      height: '30px',
                      borderRadius: '4px',
                      border: '1px solid #337ab7',
                      background: '#fff',
                      cursor: tempDocument.trim() ? 'pointer' : 'default',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <i className="dx-icon-save" style={{ fontSize: '14px', color: tempDocument.trim() ? '#337ab7' : '#ccc' }} />
                  </button>
                  <button 
                    type="button"
                    onClick={handleCancelEditDocument}
                    title="Cancelar edición"
                    className="dx-button dx-button-mode-outlined dx-button-danger"
                    style={{
                      minWidth: '30px',
                      width: '30px',
                      height: '30px',
                      borderRadius: '4px',
                      border: '1px solid #d9534f',
                      background: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <i className="dx-icon-close" style={{ fontSize: '14px', color: '#d9534f' }} />
                  </button>
                </>
              ) : (
                <button 
                  type="button"
                  onClick={handleStartEditDocument}
                  title="Editar documento"
                  className="dx-button dx-button-mode-outlined dx-button-default"
                  style={{
                    minWidth: '30px',
                    width: '30px',
                    height: '30px',
                    borderRadius: '4px',
                    border: '1px solid #337ab7',
                    background: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <i className="dx-icon-edit" style={{ fontSize: '14px', color: '#337ab7' }} />
                </button>
              )}
            </div>
          </div>
        </Item>
        {info
          ?.filter((x) => x.Dfc_Equivalencia !== 'Pc_Foto' && x.TipoCampo.Tcm_Equivalencia !== 'dxTextArea')
          .sort((x) => x.Dfc_Orden)
          .map((x) => {
            const currentDep = info.find((y) => x.Dfc_DependencyDefinition === y.Dfc_Id)?.Dfc_Equivalencia;

            // Campo especial para fecha de nacimiento
            if (x.Dfc_Equivalencia === 'Pc_FechaNacimiento') {
              return (
                <Item key={x.Dfc_Id} colSpan={2}>
                  <BirthDateField                  
                    value={x.Dfc_Dinamico && data.AditionalFields ? data.AditionalFields[x.Dfc_Equivalencia] || null : data[x.Dfc_Equivalencia]}
                    onValueChange={(newValue) => {
                      const setValue = setData(x.Dfc_Dinamico ? `AditionalFields.${x.Dfc_Equivalencia}` : x.Dfc_Equivalencia, x.Dfc_Cast);
                      setValue(newValue);
                    }}
                    isEditing={!isEditingDocument && true }
                    label={x.Dfc_Label || 'Fecha de Nacimiento'}
                  />
                </Item>
              );
            }

            return (
              <Item key={x.Dfc_Id} colSpan={1}>
                <CustomFormControl valueDepency={x.Dfc_Dependency && currentDep && data.AditionalFields ? data.AditionalFields[currentDep] : undefined} value={x.Dfc_Dinamico && data.AditionalFields ? data.AditionalFields[x.Dfc_Equivalencia] || null : data[x.Dfc_Equivalencia]} data={x} onValueChange={setData(x.Dfc_Dinamico ? `AditionalFields.${x.Dfc_Equivalencia}` : x.Dfc_Equivalencia, x.Dfc_Cast)} isEditing={!isEditingDocument && true} />
              </Item>
            );
          })}
      </GroupItem>
      <GroupItem>
        {info
          ?.filter((x) => x.TipoCampo.Tcm_Equivalencia === 'dxTextArea')
          .sort((x) => x.Dfc_Orden)
          .map((x) => {
            return (
              <Item key={x.Dfc_Id}>
                <CustomFormControl value={x.Dfc_Dinamico && data.AditionalFields ? data.AditionalFields[x.Dfc_Equivalencia] : data[x.Dfc_Equivalencia]} data={x} onValueChange={setData(x.Dfc_Dinamico ? `AditionalFields.${x.Dfc_Equivalencia}` : x.Dfc_Equivalencia, x.Dfc_Cast)} isEditing={!isEditingDocument && true} />
              </Item>
            );
          })}
      </GroupItem>
    </Form>
    </>
  );
};

export default Index;
